import Phaser from "phaser";
import type { TabContentRect, TabViewContract } from "@yjsgames/phaser-ui/tab-registry";
import { NINE_SLICE_BUTTON_EVENT, NineSliceButton, ScrollableList } from "@yjsgames/phaser-ui";
import type { TextDirection } from "@yjsgames/i18n";
import { type SpaceshipDefinition } from "../data/spaceshipData.js";
import { getTextDirection, t } from "../i18n/i18n.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, setButtonEnabled, SMALL_BUTTON_STYLE, TYPO } from "./uiTokens.js";

export interface SpaceshipTabData {
  readonly spaceships: readonly SpaceshipDefinition[];
  readonly currentId: string;
  readonly ownedIds: readonly string[];
  readonly unlockableIds: readonly string[];
}

export interface SpaceshipTabViewOptions {
  readonly onInfo?: (id: string) => void;
  readonly onEquip?: (id: string) => void;
}

interface RowObjects {
  readonly id: string;
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly thumb: Phaser.GameObjects.Image;
  readonly hero: Phaser.GameObjects.Image;
  readonly name: Phaser.GameObjects.Text;
  readonly grid: Phaser.GameObjects.Text;
  readonly unlock: Phaser.GameObjects.Text;
  readonly equipButton: NineSliceButton;
}

const HEADER_H = 24;
const LIST_TOP_GAP = 4;
const CARD_H = 82;
const CARD_GAP = 8;
const CARD_PAD = 10;
const THUMB_SIZE = 42;
const HERO_SIZE = 72;
const SCROLLBAR_GUTTER = 18;
const EQUIP_BUTTON_W = 68;

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(visible);
}

function gridLabel(def: SpaceshipDefinition): string {
  return `${def.gridSize.width} x ${def.gridSize.height}`;
}

/**
 * 우주선 탭 — 우주선 데이터 목록을 카드형 스크롤 리스트로 표시한다.
 */
export class SpaceshipTabView implements TabViewContract<SpaceshipTabData> {
  private readonly title: Phaser.GameObjects.Text;
  private readonly scrollList: ScrollableList;
  private readonly rows: RowObjects[] = [];
  private readonly rect: { x: number; y: number; width: number };
  private visible = false;
  private lastData: SpaceshipTabData | undefined;
  private dir: TextDirection = getTextDirection();

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly bottomY: number,
    private readonly opts: SpaceshipTabViewOptions = {},
  ) {
    this.rect = { x, y, width };
    const L = this.layout(this.dir);
    this.title = scene.add
      .text(L.title.x, L.title.y, t("tab.spaceship"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textPrimary,
      })
      .setOrigin(0.5, 0)
      .setDepth(3)
      .setVisible(false);

    this.scrollList = new ScrollableList(scene, L.scroll.x, L.scroll.y, {
      width: L.scroll.width,
      height: L.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
      itemMargin: CARD_H + CARD_GAP,
    });
    this.scrollList.setDepth(2);
    this.scrollList.setVisible(false);
    scene.add.existing(this.scrollList);
  }

  update(data: SpaceshipTabData): void {
    this.lastData = data;
    this.rebuildRows(data);
    this.applyRowData(data);
    this.relayout(this.dir);
    this.setVisible(this.visible);
  }

  applyLanguage(): void {
    this.title.setText(t("tab.spaceship"));
    if (this.lastData) {
      this.applyRowData(this.lastData);
    }
  }

  relayout(dir: TextDirection): void {
    this.dir = dir;
    const L = this.layout(dir);
    this.title.setPosition(L.title.x, L.title.y);
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    this.scrollList.setContentHeight(this.contentHeight(this.rows.length));

    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.rows.forEach((row, index) => {
      const R = this.rowLayout(L.scroll.width, index, dir);
      row.frame.setSize(R.frame.w, R.frame.h);
      row.frame.setStrokeStyle(
        row.id === this.lastData?.currentId ? 2 : 1,
        row.id === this.lastData?.currentId ? PALETTE_NUM.accentGold : PALETTE_NUM.gridPathGlow,
        row.id === this.lastData?.currentId ? 0.86 : 0.28,
      );
      row.name.setOrigin(R.textOriginX, 0).setAlign(R.align).setWordWrapWidth(R.textW);
      row.grid.setOrigin(R.textOriginX, 0).setAlign(R.align).setWordWrapWidth(R.textW);
      row.unlock.setOrigin(R.textOriginX, 0).setAlign(R.align).setWordWrapWidth(R.textW);
      row.equipButton.setPosition(R.equipButton.x, R.equipButton.y);
      positions.push(
        { obj: row.frame, lx: R.frame.x, ly: R.frame.y },
        { obj: row.thumb, lx: R.thumb.x, ly: R.thumb.y },
        { obj: row.hero, lx: R.hero.x, ly: R.hero.y },
        { obj: row.name, lx: R.name.x, ly: R.name.y },
        { obj: row.grid, lx: R.grid.x, ly: R.grid.y },
        { obj: row.unlock, lx: R.unlock.x, ly: R.unlock.y },
        { obj: row.equipButton, lx: R.equipButton.x, ly: R.equipButton.y },
      );
    });
    this.scrollList.relayoutContent(positions);
  }

  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.relayout(getTextDirection());
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    setVisibleActive(this.title, visible);
    this.scrollList.setVisible(visible);
    for (const row of this.rows) {
      setVisibleActive(row.frame, visible);
      setVisibleActive(row.thumb, visible);
      setVisibleActive(row.hero, visible);
      setVisibleActive(row.name, visible);
      setVisibleActive(row.grid, visible);
      setVisibleActive(row.unlock, visible);
      setVisibleActive(row.equipButton, visible);
    }
  }

  private rebuildRows(data: SpaceshipTabData): void {
    const sameRows =
      this.rows.length === data.spaceships.length && this.rows.every((row, i) => row.id === data.spaceships[i]?.id);
    if (sameRows) return;

    this.scrollList.clearContent();
    for (const row of this.rows) {
      row.frame.destroy();
      row.thumb.destroy();
      row.hero.destroy();
      row.name.destroy();
      row.grid.destroy();
      row.unlock.destroy();
      row.equipButton.destroy();
    }
    this.rows.length = 0;

    data.spaceships.forEach((def, index) => {
      const R = this.rowLayout(this.layout(this.dir).scroll.width, index, this.dir);
      const frame = this.scene.add
        .rectangle(R.frame.x, R.frame.y, R.frame.w, R.frame.h, PALETTE_NUM.cellBg, 0.88)
        .setOrigin(0, 0)
        .setDepth(1)
        .setInteractive({ useHandCursor: true });
      frame.on("pointerup", () => this.opts.onInfo?.(def.id));
      const thumb = this.scene.add
        .image(R.thumb.x, R.thumb.y, def.smallAssetKey)
        .setDisplaySize(THUMB_SIZE, THUMB_SIZE)
        .setDepth(3);
      const hero = this.scene.add
        .image(R.hero.x, R.hero.y, def.largeAssetKey)
        .setDisplaySize(HERO_SIZE, HERO_SIZE)
        .setAlpha(0.28)
        .setDepth(2);
      const name = this.scene.add
        .text(R.name.x, R.name.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.label,
          color: PALETTE.textPrimary,
          align: R.align,
          wordWrap: { width: R.textW },
        })
        .setOrigin(R.textOriginX, 0)
        .setDepth(4);
      const grid = this.scene.add
        .text(R.grid.x, R.grid.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
          align: R.align,
          wordWrap: { width: R.textW },
        })
        .setOrigin(R.textOriginX, 0)
        .setDepth(4);
      const unlock = this.scene.add
        .text(R.unlock.x, R.unlock.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.accentAmber,
          align: R.align,
          wordWrap: { width: R.textW },
        })
        .setOrigin(R.textOriginX, 0)
        .setDepth(4);
      const equipButton = new NineSliceButton(this.scene, R.equipButton.x, R.equipButton.y, {
        style: SMALL_BUTTON_STYLE,
        label: t("spaceship.detail.equip"),
        widthOverride: EQUIP_BUTTON_W,
        heightOverride: 24,
      }).setDepth(5);
      equipButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => this.opts.onEquip?.(def.id));

      this.scrollList.addContent(frame, R.frame.x, R.frame.y);
      this.scrollList.addContent(thumb, R.thumb.x, R.thumb.y);
      this.scrollList.addContent(hero, R.hero.x, R.hero.y);
      this.scrollList.addContent(name, R.name.x, R.name.y);
      this.scrollList.addContent(grid, R.grid.x, R.grid.y);
      this.scrollList.addContent(unlock, R.unlock.x, R.unlock.y);
      this.scrollList.addContent(equipButton, R.equipButton.x, R.equipButton.y);
      this.rows.push({ id: def.id, frame, thumb, hero, name, grid, unlock, equipButton });
    });
  }

  private applyRowData(data: SpaceshipTabData): void {
    data.spaceships.forEach((def, i) => {
      const row = this.rows[i];
      if (!row) return;
      row.thumb.setTexture(def.smallAssetKey);
      row.hero.setTexture(def.largeAssetKey);
      row.name.setText(t(def.nameKey));
      row.grid.setText(gridLabel(def));
      if (def.id === data.currentId) {
        row.unlock.setText(t("spaceship.status.current"));
        row.equipButton.setLabel(t("spaceship.detail.equipped"));
        setButtonEnabled(row.equipButton, false);
      } else if (data.ownedIds.includes(def.id)) {
        row.unlock.setText(t("spaceship.status.owned"));
        row.equipButton.setLabel(t("spaceship.detail.equip"));
        setButtonEnabled(row.equipButton, true);
      } else if (data.unlockableIds.includes(def.id)) {
        row.unlock.setText(t(def.unlockCondition.messageKey));
        row.equipButton.setLabel(t("spaceship.detail.unlockEquip"));
        setButtonEnabled(row.equipButton, true);
      } else {
        row.unlock.setText(t(def.unlockCondition.messageKey));
        row.equipButton.setLabel(t("spaceship.detail.locked"));
        setButtonEnabled(row.equipButton, false);
      }
    });
  }

  private layout(dir: TextDirection): {
    readonly title: { readonly x: number; readonly y: number };
    readonly scroll: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
    readonly dir: TextDirection;
  } {
    const scrollY = this.rect.y + HEADER_H + LIST_TOP_GAP;
    return {
      title: { x: this.rect.x + this.rect.width / 2, y: this.rect.y },
      scroll: {
        x: this.rect.x,
        y: scrollY,
        width: this.rect.width,
        height: Math.max(80, this.bottomY - scrollY - 6),
      },
      dir,
    };
  }

  private rowLayout(
    scrollWidth: number,
    index: number,
    dir: TextDirection,
  ): {
    readonly frame: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
    readonly thumb: { readonly x: number; readonly y: number };
    readonly hero: { readonly x: number; readonly y: number };
    readonly name: { readonly x: number; readonly y: number };
    readonly grid: { readonly x: number; readonly y: number };
    readonly unlock: { readonly x: number; readonly y: number };
    readonly equipButton: { readonly x: number; readonly y: number };
    readonly textW: number;
    readonly textOriginX: 0 | 1;
    readonly align: "left" | "right";
  } {
    const y = index * (CARD_H + CARD_GAP);
    const frameW = Math.max(220, scrollWidth - SCROLLBAR_GUTTER);
    const frame = { x: 4, y, w: frameW, h: CARD_H };
    const ltr = dir !== "rtl";
    const thumbX = ltr ? frame.x + CARD_PAD + THUMB_SIZE / 2 : frame.x + frame.w - CARD_PAD - THUMB_SIZE / 2;
    const heroX = ltr ? frame.x + frame.w - CARD_PAD - HERO_SIZE / 2 : frame.x + CARD_PAD + HERO_SIZE / 2;
    const buttonX = ltr ? frame.x + frame.w - CARD_PAD - EQUIP_BUTTON_W / 2 : frame.x + CARD_PAD + EQUIP_BUTTON_W / 2;
    const textX = ltr ? thumbX + THUMB_SIZE / 2 + 10 : thumbX - THUMB_SIZE / 2 - 10;
    const heroEdge = ltr
      ? Math.min(heroX - HERO_SIZE / 2, buttonX - EQUIP_BUTTON_W / 2) - 10
      : Math.max(heroX + HERO_SIZE / 2, buttonX + EQUIP_BUTTON_W / 2) + 10;
    const textW = Math.max(70, Math.abs(heroEdge - textX));
    return {
      frame,
      thumb: { x: thumbX, y: y + CARD_H / 2 },
      hero: { x: heroX, y: y + CARD_H / 2 },
      name: { x: textX, y: y + 12 },
      grid: { x: textX, y: y + 34 },
      unlock: { x: textX, y: y + 54 },
      equipButton: { x: buttonX, y: y + CARD_H - 18 },
      textW,
      textOriginX: ltr ? 0 : 1,
      align: ltr ? "left" : "right",
    };
  }

  private contentHeight(count: number): number {
    if (count <= 0) return 0;
    return count * CARD_H + (count - 1) * CARD_GAP + 4;
  }
}
